import { useEffect, useState } from "react";

const KEY = "ljp-a11y";
type Prefs = { contrast: boolean; large: boolean; libras: boolean };

/** Loads the free VLibras widget (Brazilian Sign Language) only when the visitor asks for it. */
function loadVLibras() {
  if (document.getElementById("vlibras-script")) return;
  const wrap = document.createElement("div");
  wrap.setAttribute("vw", "");
  wrap.className = "enabled";
  wrap.innerHTML =
    '<div vw-access-button class="active"></div><div vw-plugin-wrapper><div class="vw-plugin-top-wrapper"></div></div>';
  document.body.appendChild(wrap);
  const s = document.createElement("script");
  s.id = "vlibras-script";
  s.src = "https://vlibras.gov.br/app/vlibras-plugin.js";
  s.onload = () => {
    const w = window as unknown as { VLibras?: { Widget: new (url: string) => unknown } };
    if (w.VLibras) new w.VLibras.Widget("https://vlibras.gov.br/app");
    window.dispatchEvent(new Event("load"));
  };
  document.body.appendChild(s);
}

/** Floating accessibility options: high contrast, larger text, read aloud and Libras. */
export function AccessibilityMenu() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>({ contrast: false, large: false, libras: false });
  const [speaking, setSpeaking] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(KEY) ?? "null") as Prefs | null;
      if (saved) setPrefs(saved);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("a11y-contrast", prefs.contrast);
    root.classList.toggle("a11y-large", prefs.large);
    if (prefs.libras) loadVLibras();
    window.localStorage.setItem(KEY, JSON.stringify(prefs));
  }, [prefs]);

  const toggle = (k: keyof Prefs) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

  const readAloud = () => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    if (speaking) {
      synth.cancel();
      setSpeaking(false);
      return;
    }
    const text = (document.querySelector("main") ?? document.body).textContent?.replace(/\s+/g, " ").trim() ?? "";
    const u = new SpeechSynthesisUtterance(text.slice(0, 12000));
    u.lang = document.documentElement.lang || "en";
    u.onend = () => setSpeaking(false);
    synth.speak(u);
    setSpeaking(true);
  };

  const row =
    "flex min-h-11 w-full items-center justify-between gap-6 px-4 text-left font-mono text-[11px] uppercase tracking-[0.18em] text-bone hover:bg-bone/10 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-bone";

  return (
    <div className="fixed bottom-24 right-4 z-[95] md:bottom-28 md:right-[3vw]">
      {open ? (
        <div id="a11y-panel" role="group" aria-label="Accessibility" className="mb-2 w-64 border border-bone/30 bg-ink py-2">
          <button type="button" className={row} aria-pressed={prefs.contrast} onClick={() => toggle("contrast")}>
            High contrast <span aria-hidden="true">{prefs.contrast ? "●" : "○"}</span>
          </button>
          <button type="button" className={row} aria-pressed={prefs.large} onClick={() => toggle("large")}>
            Larger text <span aria-hidden="true">{prefs.large ? "●" : "○"}</span>
          </button>
          <button type="button" className={row} aria-pressed={speaking} onClick={readAloud}>
            {speaking ? "Stop reading" : "Read page aloud"} <span aria-hidden="true">♪</span>
          </button>
          <button type="button" className={row} aria-pressed={prefs.libras} onClick={() => toggle("libras")}>
            Libras (VLibras) <span aria-hidden="true">{prefs.libras ? "●" : "○"}</span>
          </button>
        </div>
      ) : null}
      <button
        type="button"
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-label="Accessibility options"
        onClick={() => setOpen((o) => !o)}
        className="ml-auto flex h-11 w-11 items-center justify-center border border-bone/30 bg-ink/80 text-bone backdrop-blur-md hover:bg-bone/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bone"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
          <circle cx="12" cy="4.5" r="1.8" />
          <path d="M5 8.5l7 1.5 7-1.5M12 10v4.5M12 14.5l-3 6M12 14.5l3 6" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
